import { Fragment } from 'react'
import type { ReactElement } from 'react'
import styled from 'styled-components'

import { theme } from '@/styles/theme'

import { SectionTitle } from '@/components/elements/SectionTitle'
import { Spacer } from '@/components/elements/Spacer'
import { TitleLayout } from '@/components/enlayout'
import { Head } from '@/components/enlayouts/Head'
import { Layout } from '@/components/enlayouts/Ir'
import { BulletedList } from '@/components/enlayouts/Ir/BulletedList'
import { DHeading } from '@/components/enlayouts/Ir/Heading'
import { AnotationText } from '@/components/enlayouts/Ir/Mpocket/AnotationText'
import { Paragraph } from '@/components/enlayouts/Ir/Paragraph'

import { useMpocketScript } from '@/hooks/useMpocketScript'

// 英語のスタイルであるグラフを埋め込むため、styleを追加
type ChartItem = {
  title: string
  id: string
  style: string
}

type ChartSection = {
  anchorId: string
  title: string
  annotationId: string
  afterChartAnnotationId?: string
  chartList: ChartItem[]
  chartType?: 'default' | 'tall'
}

const consolidatedChartList: ChartItem[] = [
  {
    title: 'Net Sales',
    id: 'irp-chart-graph-uriage',
    style: 'graph-uriage_en-style',
  },
  {
    title: 'Operating Income',
    id: 'irp-chart-graph-eigyo',
    style: 'graph-eigyo_en-style',
  },
  {
    title: 'Ordinary Income',
    id: 'irp-chart-graph-keijo',
    style: 'graph-keijo_en-style',
  },
  {
    title: 'Profit Attributable to Owners of Parent',
    id: 'irp-chart-graph-oya',
    style: 'graph-oya_en-style',
  },
  {
    title: 'Total Assets',
    id: 'irp-chart-graph-soshisan',
    style: 'graph-soshisan_en-style',
  },
  {
    title: 'Net Assets',
    id: 'irp-chart-graph-junshisan',
    style: 'graph-junshisan_en-style',
  },
  {
    title: 'Number of Stores',
    id: 'irp-chart-graph-tenpo',
    style: 'graph-tenpo_en-style',
  },
]

const trialChartList: ChartItem[] = [
  {
    title: 'Net Sales',
    id: 'irp-chart-graph-uriage_trial',
    style: 'graph-uriage_trial_en-style',
  },
  {
    title: 'Operating Income',
    id: 'irp-chart-graph-eigyo_trial',
    style: 'graph-eigyo_trial_en-style',
  },
  {
    title: 'Number of Stores',
    id: 'irp-chart-graph-tenpo_trial',
    style: 'graph-tenpo_trial_en-style',
  },
]

const seiyuChartList: ChartItem[] = [
  {
    title: 'Net Sales',
    id: 'irp-chart-graph-uriage_seiyu',
    style: 'graph-uriage_seiyu_en-style',
  },
  {
    title: 'Operating Income',
    id: 'irp-chart-graph-eigyo_seiyu',
    style: 'graph-eigyo_seiyu_en-style',
  },
  {
    title: 'Number of Stores',
    id: 'irp-chart-graph-tenpo_seiyu',
    style: 'graph-tenpo_seiyu_en-style',
  },
]

const chartSections: ChartSection[] = [
  {
    anchorId: 'hilight01',
    title: 'Consolidated',
    annotationId: 'irp-highlight-table-review1_en',
    afterChartAnnotationId: 'irp-highlight-table-review2',
    chartList: consolidatedChartList,
  },
  {
    anchorId: 'hilight02',
    title: 'TRIAL',
    annotationId: 'irp-highlight-table-review_trial_en',
    chartList: trialChartList,
    chartType: 'tall',
  },
  {
    anchorId: 'hilight03',
    title: 'SEIYU',
    annotationId: 'irp-highlight-table-review_seiyu_en',
    chartList: seiyuChartList,
    chartType: 'tall',
  },
]

export const ReviewPage = () => {
  useMpocketScript(
    '//irpocket.com/141A/irpocket/ir-financial-review_en_r2026.js'
  )
  // useMpocketScript('//irpocket.com/5882/irpocket/ir-financial-review_en.js')
  return (
    <Layout>
      <link
        rel="stylesheet"
        href="//irpocket.com/141A/irpocket/irp-style_en.css"
      ></link>
      <SectionTitle
        enTitleText="Financial Highlights"
        subTitleText=""
        color="normal"
      />
      <Spacer h={{ sp: 70, pc: 120 }} />
      <ul id="irp-hilightTab">
        {chartSections.map(({ anchorId, title }) => (
          <li key={anchorId}>
            <a href={`#${anchorId}`}>{title}</a>
          </li>
        ))}
      </ul>
      {chartSections.map(
        ({
          anchorId,
          title,
          annotationId,
          afterChartAnnotationId,
          chartList,
          chartType = 'default',
        }) => (
          <Fragment key={anchorId}>
            <Spacer h={{ sp: 30, pc: 60 }} />
            <DHeading id={anchorId}>{title}</DHeading>
            <Spacer h={{ sp: 30, pc: 60 }} />
            <AnotationText id={annotationId} />
            <Spacer h={{ sp: 30, pc: 50 }} />
            <BulletedList>
              <li>Net sales and income are cumulative quarterly totals.</li>
            </BulletedList>
            <Spacer h={{ sp: 70, pc: 100 }} />
            <ChartWrapper>
              {chartList.map(({ title, id, style }) => (
                <ChartContainer
                  key={id}
                  className={
                    chartType === 'tall'
                      ? 'Review__ChartContainer-sc-1pa8bt-1 cmckYz'
                      : 'Review__ChartContainer-sc-1lxl9dc-1 ccZwcW'
                  }
                >
                  <Title>{title}</Title>
                  <Spacer h={{ sp: 20, pc: 36 }} />
                  {chartType === 'tall' ? (
                    <TallChart
                      id={id}
                      data-style={style}
                      className="Review__Chart-sc-1pa8bt-3 dkYbNt"
                    />
                  ) : (
                    <Chart
                      id={id}
                      data-style={style}
                      className="Review__Chart-sc-1lxl9dc-3 ssflk"
                    />
                  )}
                </ChartContainer>
              ))}
            </ChartWrapper>
            {afterChartAnnotationId && (
              <>
                <Spacer h={{ sp: 80, pc: 100 }} />
                <AnotationText id={afterChartAnnotationId} />
              </>
            )}
          </Fragment>
        )
      )}
      <Spacer h={{ sp: 80, pc: 100 }} />
      <AnotationText id="irp-highlight-table-review2_en" />
      <Paragraph>
        Clicking on the legend at the bottom of the graph toggles the graph
        display on and off. Hovering the cursor over the graph will display the
        period covered and the numbers.
      </Paragraph>
    </Layout>
  )
}

ReviewPage.getLayout = (page: ReactElement) => {
  const breadCrumbs = [
    { name: 'Home', path: '/en/' },
    { name: 'Investor Relations', path: '/en/ir' },
    { name: 'Financial Information', path: '/en/ir/financial' },
    { name: 'Financial Highlights', path: '' },
  ]

  return (
    <>
      <Head
        title="Financial Highlights"
        description="The summary of the quarterly financial results."
        ogp={{ url: 'en/ir/financial/review' }}
      />
      <TitleLayout
        title="Investor Relations"
        subTitle=""
        breadCrumbs={breadCrumbs}
      >
        {page}
      </TitleLayout>
    </>
  )
}

const ChartWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 80px;

  @media screen and (max-width: 1047px) {
    grid-template-columns: repeat(1, 1fr);
    gap: 50px;
  }
`

const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const Title = styled.h3`
  font-size: 24px;
  font-weight: bold;
  color: ${theme.colors.trialBlue};
  line-height: 33.6px;

  @media screen and (max-width: 1047px) {
    font-size: 18px;
    line-height: 23.4px;
  }
`

const Chart = styled.div`
  height: 293px;
  width: 470px;

  @media screen and (max-width: 1047px) {
    height: 185.32px;
    width: 100%;
  }
`

const TallChart = styled.div`
  height: 400px;
  width: 100%;

  @media screen and (max-width: 1047px) {
    height: 400px;
  }
`
